import { StatusBadge } from "@/components/StatusBadge";
import { SourceLink } from "@/components/primitives";
import { AudienceSelector } from "@/components/AudienceSelector";
import { CheckCircle, Circle, DotsThreeCircle } from "@phosphor-icons/react";

const PHASE_STATUSES = ["Not confirmed", "In progress", "Complete"];

function PhaseDot({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "complete") return <CheckCircle size={22} weight="fill" className="text-[#8FB97A]" />;
  if (s === "in progress") return <DotsThreeCircle size={22} weight="fill" className="text-[#F59E0B]" />;
  return <Circle size={22} className="text-[#5a5a54]" />;
}

export function ProjectJourney({ phases, editable, onChangeStatus, onChangeAudiences }) {
  return (
    <div className="relative" data-testid="project-journey">
      <div className="absolute left-[10px] top-2 bottom-2 w-px bg-[#2C2C2A] md:left-[10px]" />
      <div className="space-y-8">
        {phases.map((phase, idx) => (
          <div key={phase.slug} className="relative pl-10" data-testid={`journey-phase-${phase.slug}`}>
            <div className="absolute left-0 top-0 bg-[#121211]">
              <PhaseDot status={phase.status} />
            </div>
            <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-baseline gap-3">
                  <span className="cm-label">{String(idx + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl">{phase.label}</h3>
                </div>
                {editable ? (
                  <select
                    data-testid={`journey-status-select-${phase.slug}`}
                    value={phase.status}
                    onChange={(e) => onChangeStatus(phase.id, e.target.value)}
                    className="rounded-sm border border-[#2C2C2A] bg-[#121211] px-2 py-1 text-xs text-[#F9F9F8] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                  >
                    {PHASE_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <StatusBadge status={phase.status} data-testid={`journey-badge-${phase.slug}`} />
                )}
              </div>
              <p className="mt-2 text-sm text-[#A1A19D]">{phase.description}</p>

              {editable && onChangeAudiences && (
                <div className="mt-3" data-testid={`journey-audience-${phase.slug}`}>
                  <AudienceSelector value={phase.audiences || ["internal"]} onChange={(a) => onChangeAudiences(phase.id, a)} />
                </div>
              )}

              {phase.milestones && phase.milestones.length > 0 ? (
                <div className="mt-4 border-t border-[#2C2C2A] pt-4">
                  <div className="cm-label mb-2">Evidence-linked milestones</div>
                  <ul className="space-y-2">
                    {phase.milestones.map((m) => (
                      <li key={m.id || m.label} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-[#F9F9F8]">{m.label}</span>
                          {m.date && <span className="cm-label">· {m.date}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={m.status} />
                          {m.source_url !== undefined && <SourceLink url={m.source_url} />}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-4 border-t border-[#2C2C2A] pt-4 text-xs italic text-[#6b6b66]">
                  No confirmed milestones recorded for this phase.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
